import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { useSendMessage } from '../hooks/useMessages'
import { RecipientSelector } from './RecipientSelector'
import type { SendMessageRequest } from '../types/message'

interface MessageComposeFormProps {
  onSuccess?: () => void
  onCancel?: () => void
}

export function MessageComposeForm({ onSuccess, onCancel }: MessageComposeFormProps) {
  const { t } = useTranslation('messages')
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [recipientType, setRecipientType] = useState<'all' | 'individual'>('all')
  const [selectedUsers, setSelectedUsers] = useState<number[]>([])

  const { mutate: sendMessage, isPending } = useSendMessage()

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!title.trim() || !content.trim()) {
      toast.warning(t('compose.validationTitleContent'))
      return
    }

    if (recipientType === 'individual' && selectedUsers.length === 0) {
      toast.warning(t('compose.validationRecipient'))
      return
    }

    const request: SendMessageRequest = {
      title: title.trim(),
      content: content.trim(),
      recipientType,
      ...(recipientType === 'individual' && { recipientIds: selectedUsers }),
    }

    sendMessage(request, {
      onSuccess: () => {
        toast.success(t('compose.success'))
        onSuccess?.()
      },
      onError: (error) => {
        toast.error(t('compose.error'))
        console.error(error)
      }
    })
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('compose.titleLabel')}
        </label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={100}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder={t('compose.titlePlaceholder')}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('compose.contentLabel')}
        </label>
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          maxLength={1000}
          rows={6}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder={t('compose.contentPlaceholder')}
        />
        <p className="text-xs text-gray-500 mt-1">{t('compose.charCount', { current: content.length, max: 1000 })}</p>
      </div>

      <RecipientSelector
        recipientType={recipientType}
        onRecipientTypeChange={setRecipientType}
        selectedUsers={selectedUsers}
        onUsersChange={setSelectedUsers}
      />

      <div className="flex justify-end gap-3">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
          >
            {t('compose.cancel')}
          </button>
        )}
        <button
          type="submit"
          disabled={isPending}
          className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          {isPending ? t('compose.sending') : t('compose.send')}
        </button>
      </div>
    </form>
  )
}
